import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import type {
  AnnouncementSummary,
  UnreadCountResponse,
} from '@df-gate-share/shared';
import { apiClient } from '../lib/api';
import { useAuth } from './AuthContext';

interface AnnouncementContextType {
  /** 公開中お知らせのうち未読件数 */
  unreadCount: number;
  /** 重要度 high かつ未読のお知らせ（ログイン後モーダル表示対象） */
  highPriorityUnread: AnnouncementSummary[];
  /** バナー表示対象（未読があるとき、最新の未読お知らせ 1 件） */
  latestUnread: AnnouncementSummary | null;
  /** 未読件数・未読お知らせを再取得する */
  refreshUnread: () => Promise<void>;
  /**
   * 既読 API 呼び出し後、サーバーが返す最新の未読件数を反映する。
   * 併せてローカルの未読集合（highPriorityUnread/latestUnread）から該当を除去する。
   */
  applyRead: (announcementId: string, unreadCount: number) => void;
}

export const AnnouncementContext = createContext<
  AnnouncementContextType | undefined
>(undefined);

interface AnnouncementProviderProps {
  children: ReactNode;
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [highPriorityUnread, setHighPriorityUnread] = useState<
    AnnouncementSummary[]
  >([]);
  const [latestUnread, setLatestUnread] = useState<AnnouncementSummary | null>(
    null
  );

  const refreshUnread = useCallback(async () => {
    try {
      const response = await apiClient.get(
        '/api/v1/announcements/unread-count'
      );
      const data: UnreadCountResponse = response.data;
      setUnreadCount(data.unreadCount ?? 0);
      const high = data.highPriorityUnread ?? [];
      setHighPriorityUnread(high);
      // バナーは最新の未読お知らせ 1 件を提示する。
      // unread-count は高優先度未読のみ返すため、それを優先し、
      // なければ一覧を取得して最新の未読を拾う。
      if (high.length > 0) {
        setLatestUnread(high[0]);
      } else if ((data.unreadCount ?? 0) > 0) {
        try {
          const listResponse = await apiClient.get('/api/v1/announcements');
          const items: AnnouncementSummary[] =
            listResponse.data?.announcements ?? [];
          const firstUnread = items.find((a) => !a.isRead) ?? null;
          setLatestUnread(firstUnread);
        } catch {
          setLatestUnread(null);
        }
      } else {
        setLatestUnread(null);
      }
    } catch {
      // 未読件数の取得失敗は致命的ではないため、状態は据え置く。
    }
  }, []);

  const applyRead = useCallback(
    (announcementId: string, newUnreadCount: number) => {
      setUnreadCount(newUnreadCount);
      setHighPriorityUnread((prev) =>
        prev.filter((a) => a.announcementId !== announcementId)
      );
      setLatestUnread((prev) =>
        prev && prev.announcementId === announcementId ? null : prev
      );
    },
    []
  );

  // 認証状態に応じて未読情報を取得/クリアする。
  useEffect(() => {
    if (isAuthenticated) {
      refreshUnread();
    } else {
      setUnreadCount(0);
      setHighPriorityUnread([]);
      setLatestUnread(null);
    }
  }, [isAuthenticated, refreshUnread]);

  const value: AnnouncementContextType = {
    unreadCount,
    highPriorityUnread,
    latestUnread,
    refreshUnread,
    applyRead,
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
};

export const useAnnouncements = (): AnnouncementContextType => {
  const context = useContext(AnnouncementContext);
  if (context === undefined) {
    throw new Error(
      'useAnnouncements must be used within an AnnouncementProvider'
    );
  }
  return context;
};

/**
 * プロバイダー未提供でも例外を投げず、無効値（未読なし）にフォールバックする版。
 * Header など「未読バッジの付随表示」だけを行う消費者向け。
 * （バッジ表示は補助的機能で、プロバイダー外でも致命的にしないため）
 */
const NO_ANNOUNCEMENTS: AnnouncementContextType = {
  unreadCount: 0,
  highPriorityUnread: [],
  latestUnread: null,
  refreshUnread: async () => {},
  applyRead: () => {},
};

export const useAnnouncementsOptional = (): AnnouncementContextType => {
  const context = useContext(AnnouncementContext);
  return context ?? NO_ANNOUNCEMENTS;
};
